import { Component } from '@theme/component';

const CM_FALLBACK_SELECTOR = '[data-size-guide-cm-fallback="true"]';
// Whole header, not any word in it: "Size" holds alpha labels, "Pant Size" holds inches.
const SIZE_COLUMN_HEADER_PATTERN = /^(?:size|sizes|alpha|alpha size|letter|letter size|numeric|fit|cut)$/i;
// Anywhere in the header: "US Men's Shoe Size" is a size designation, not a measurement.
const REGION_SIZE_HEADER_PATTERN = /\b(?:us|uk|eu)\b/i;
// Not lengths. A cell in one of these converts only when it names pounds; otherwise an age
// or a unitless weight would be multiplied as though it were inches.
const NON_LENGTH_HEADER_PATTERN = /^(?:age|weight)\b/i;
const POUNDS_PATTERN = /\blbs?\b/i;
const POUNDS_TO_KILOGRAMS = 0.45359237;
// A bare `in` only counts as a unit when it trails a number; alone it is usually the
// preposition ("Sits low in the waist").
const UNIT_LABEL_PATTERNS = [
  [/\(\s*(?:in|inch|inches|ft|feet)\.?\s*\)/gi, '(cm)'],
  [/\(\s*lbs?\.?\s*\)/gi, '(kg)'],
  [/\b(?:inches|inch)\b/gi, 'cm'],
  [/(\d\s*)in\.?\b/gi, '$1cm'],
  // Dropped, not swapped — the header already carries the unit.
  [/(\d)\s*(?:"|\u2033|\u201d|'')/g, '$1'],
];

/** @type {Map<string, string>} Guide markup by handle, shared by every drawer on the page. */
const guideCache = new Map();

/** Guides filled so far, counted only to keep their ids apart. */
let instances = 0;

/**
 * Size guide tabs custom element.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tabPanel - Tab content panels
 *
 * @extends {Component<Refs>}
 */
class SizeGuideTabs extends Component {
  /** Point the element at another style and it refetches; that is the whole contract. */
  static observedAttributes = ['data-handle'];

  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  /** @type {string | null} The style whose guide is in here, or on its way in. */
  #handle = null;

  /** Suffix keeping this instance's ids clear of the other drawers'. */
  #scope = `sg${(instances += 1)}`;

  #handleDialogOpen = () => {
    this.initializeCentimeterFallbackTables();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#syncDialogOpenListener();
    this.initializeCentimeterFallbackTables();

    // An attribute set before the element upgraded fired no callback of its own.
    const { handle } = this.dataset;
    if (handle) this.#loadGuide(handle);
  }

  /**
   * @param {string} name
   * @param {string | null} _previous
   * @param {string | null} next
   */
  attributeChangedCallback(name, _previous, next) {
    // Before upgrade there is nothing to fill; the connected callback picks it up.
    if (name === 'data-handle' && next && this.isConnected) this.#loadGuide(next);
  }

  /**
   * Fill this element with one style's size guide.
   *
   * Only ever runs where a `handle` is set. A guide rendered by the server arrives with its
   * content already inside and carries no handle, so it is left exactly as it is.
   *
   * @param {string} handle - Product handle of the style whose guide to show.
   */
  async #loadGuide(handle) {
    if (this.#handle === handle) return;
    this.#handle = handle;

    const html = await fetchSizeGuide(handle);

    // Clicking through styles outruns the network, and a stale answer would win.
    if (this.#handle !== handle) return;

    this.innerHTML = html;
    this.#scopeIds();
    // A style with no guide has nothing to open.
    this.hidden = html === '';

    // Setting markup is not a morph, so nothing else re-syncs the refs behind it.
    this.updatedCallback();

    this.dispatchEvent(
      new CustomEvent('size-guide:loaded', {
        bubbles: true,
        detail: { handle, hasGuide: html !== '' },
      })
    );
  }

  /**
   * Suffix every id the fetched markup carries, and every reference to one.
   *
   * The view names ids after the product, so two drawers showing the same style would
   * share them — and same-named radios are a single group however far apart they sit,
   * which leaves the inches/cm toggle of all but the last with nothing selected.
   */
  #scopeIds() {
    for (const attribute of ['id', 'for', 'name', 'aria-controls', 'aria-labelledby']) {
      for (const element of this.querySelectorAll(`[${attribute}]`)) {
        element.setAttribute(attribute, `${element.getAttribute(attribute)}-${this.#scope}`);
      }
    }
  }

  /**
   * Re-syncs refs and re-derives the cm fallback table after a morph.
   * Called by hand from variant-picker.js — morph never invokes it.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#syncDialogOpenListener();
    this.initializeCentimeterFallbackTables();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDialogOpenListener();
  }

  /**
   * Called by the on:click binding on each tab button.
   * @param {MouseEvent} event
   */
  activateTab(event) {
    const index = parseInt(/** @type {HTMLElement} */ (event.target).dataset.index ?? '0', 10);

    this.#selectTab(index);
  }

  /**
   * Arrow/Home/End move focus between tabs and select the focused one.
   * Required once `role="tablist"` is declared — a tablist is a single tab stop.
   *
   * @param {KeyboardEvent} event
   */
  moveTabFocus(event) {
    const buttons = this.#tabButtons();
    if (buttons.length === 0) return;

    const current = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const from = current === -1 ? 0 : current;

    let next = from;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (from + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (from - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectTab(next);
    buttons[next]?.focus();
  }

  /**
   * Queried, not a ref — the tabs sit inside `drag-scroll`, so `ref` binds to that component.
   *
   * @returns {HTMLElement[]}
   */
  #tabButtons() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /**
   * @param {number} index
   */
  #selectTab(index) {
    const panels = this.refs.tabPanel ?? [];
    if (panels.length === 0) return;

    this.#tabButtons().forEach((button, i) => {
      const isSelected = i === index;
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      button.tabIndex = isSelected ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }

  /**
   * Rebuilds every cm fallback table fresh from its sibling inches table, which this
   * conversion never writes to. Regenerating rather than converting in place is what makes
   * this safe to call repeatedly — a morph restores unconverted server HTML, so there is no
   * "already converted" state to track.
   */
  initializeCentimeterFallbackTables() {
    const fallbackTables = this.querySelectorAll(CM_FALLBACK_SELECTOR);

    fallbackTables.forEach((fallbackTable) => {
      if (!(fallbackTable instanceof HTMLElement)) return;

      const inchTable = fallbackTable.parentElement?.querySelector('.size-guide-measurements__table--in table');
      if (!(inchTable instanceof HTMLTableElement)) return;

      const clonedTable = /** @type {HTMLTableElement} */ (inchTable.cloneNode(true));
      convertTableToCentimeters(clonedTable);
      fallbackTable.replaceChildren(clonedTable);
    });
  }

  /**
   * A native `<dialog>` keeps its children when hidden, so a morph while it's closed leaves
   * stale content behind. Re-deriving on open covers that regardless of what caused it.
   */
  #syncDialogOpenListener() {
    const nextDialogComponent = this.closest('dialog-component');

    if (nextDialogComponent === this.#dialogComponent) return;

    this.#removeDialogOpenListener();

    if (nextDialogComponent instanceof HTMLElement) {
      nextDialogComponent.addEventListener('dialog:open', this.#handleDialogOpen);
      this.#dialogComponent = nextDialogComponent;
    }
  }

  #removeDialogOpenListener() {
    if (!(this.#dialogComponent instanceof HTMLElement)) return;

    this.#dialogComponent.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.#dialogComponent = null;
  }
}

if (!customElements.get('size-guide-tabs')) {
  customElements.define('size-guide-tabs', SizeGuideTabs);
}

/**
 * One style's size guide markup, fetched once per page.
 *
 * The empty string is cached for a style with no guide, so asking again costs nothing and
 * the answer is still "there is none".
 *
 * @param {string} handle - Product handle.
 * @returns {Promise<string>} The guide's inner markup, or '' when the style has no guide.
 */
async function fetchSizeGuide(handle) {
  const cached = guideCache.get(handle);
  if (cached !== undefined) return cached;

  let html = '';

  try {
    const response = await fetch(sizeGuideUrl(handle));
    // The view renders what goes inside this element and nothing else, so it drops
    // straight in — and a product with no guide renders nothing at all.
    if (response.ok) html = (await response.text()).trim();
  } catch {
    // A guide that cannot be fetched is treated as one that does not exist, which leaves
    // the drawer hidden rather than empty.
  }

  guideCache.set(handle, html);

  return html;
}

/**
 * `/products/<handle>` under whatever root the shop is served from, so a localized
 * storefront fetches its own copy rather than the default locale's.
 *
 * `cart_url` is the only route Liquid hands the client that carries that prefix.
 *
 * @param {string} handle
 * @returns {string}
 */
function sizeGuideUrl(handle) {
  const root = (globalThis.Theme?.routes?.cart_url ?? '/cart').replace(/\/cart\/?$/, '');

  return `${root}/products/${encodeURIComponent(handle)}?view=size-guide-html`;
}

/**
 * Convert a cloned measurements table from inches to centimeters in-place.
 *
 * @param {HTMLTableElement} table
 */
function convertTableToCentimeters(table) {
  const excludedColumns = getExcludedColumns(table);
  const rows = Array.from(table.tBodies).flatMap((tableBody) => Array.from(tableBody.rows));

  const headerRow = table.tHead?.rows?.[table.tHead.rows.length - 1];
  const headerText = (index) => normalizeText(headerRow?.cells?.[index]?.textContent);

  rows.forEach((row) => {
    Array.from(row.cells).forEach((cell, columnIndex) => {
      if (excludedColumns.has(columnIndex)) return;

      if (POUNDS_PATTERN.test(cell.textContent ?? '')) {
        replaceTextNodes(cell, convertPoundsTextToKilograms);
        return;
      }

      if (NON_LENGTH_HEADER_PATTERN.test(headerText(columnIndex))) return;

      replaceTextNodes(cell, convertInchesTextToCentimeters);
    });
  });

  // After the numbers, so a feet-inches value still has the inch mark to parse.
  updateUnitLabels(table, excludedColumns, headerText);
}

/**
 * Convert a pounds value to kilograms, relabelling the unit with it.
 *
 * @param {string} text
 * @returns {string}
 */
function convertPoundsTextToKilograms(text) {
  const converted = text.replace(/(^|[^A-Za-z0-9])(\d+(?:\.\d+)?)(?=[^A-Za-z0-9]|$)/g, (match, prefix, value) => {
    const pounds = Number(value);
    if (!Number.isFinite(pounds)) return match;

    const kilograms = pounds * POUNDS_TO_KILOGRAMS;
    const rounded = value.includes('.') ? roundTo(kilograms, 1) : Math.round(kilograms);

    return `${prefix}${rounded}`;
  });

  return converted.replace(/\blbs?\b/gi, 'kg');
}

/**
 * Replace unit labels like "Waist (in)" or "34 in" with their cm equivalent.
 *
 * Headers always relabel — they name the column the conversion applies to. Body cells only
 * relabel in columns whose numbers are actually converted, so an excluded column can't end up
 * with inch values wearing a cm label.
 *
 * @param {HTMLTableElement} table
 * @param {Set<number>} excludedColumns
 * @param {(index: number) => string} headerText
 */
function updateUnitLabels(table, excludedColumns, headerText) {
  const relabel = (/** @type {Element} */ cell) =>
    replaceTextNodes(cell, (text) => {
      let nextText = text;

      UNIT_LABEL_PATTERNS.forEach(([pattern, replacement]) => {
        nextText = nextText.replace(pattern, replacement);
      });

      return nextText;
    });

  if (table.tHead) Array.from(table.tHead.querySelectorAll('th, td')).forEach(relabel);

  Array.from(table.tBodies)
    .flatMap((tableBody) => Array.from(tableBody.rows))
    .forEach((row) => {
      Array.from(row.cells).forEach((cell, columnIndex) => {
        if (excludedColumns.has(columnIndex)) return;
        if (NON_LENGTH_HEADER_PATTERN.test(headerText(columnIndex))) return;

        relabel(cell);
      });
    });
}

/**
 * Treat the first column as labels and skip additional columns whose headers look like size labels.
 *
 * @param {HTMLTableElement} table
 * @returns {Set<number>}
 */
function getExcludedColumns(table) {
  const excludedColumns = new Set([0]);
  const headerRow = table.tHead?.rows?.[table.tHead.rows.length - 1];

  if (!headerRow) return excludedColumns;

  Array.from(headerRow.cells).forEach((cell, index) => {
    const headerText = normalizeText(cell.textContent);
    if (SIZE_COLUMN_HEADER_PATTERN.test(headerText) || REGION_SIZE_HEADER_PATTERN.test(headerText)) {
      excludedColumns.add(index);
    }
  });

  return excludedColumns;
}

/**
 * Replace each text node under an element while preserving nested markup.
 *
 * @param {Element} element
 * @param {(text: string) => string} transform
 */
function replaceTextNodes(element, transform) {
  const textNodes = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const currentText = textNode.textContent ?? '';
    const nextText = transform(currentText);

    if (nextText !== currentText) {
      textNode.textContent = nextText;
    }
  });
}

/**
 * Convert inch values in a text string into centimeter values.
 *
 * @param {string} text
 * @returns {string}
 */
function convertInchesTextToCentimeters(text) {
  if (!/\d/.test(text)) return text;

  // Per cell, not per value: "32-33 1/2" would otherwise come back "81-85.1", rounding one
  // end and not the other. Metric is decimal, so the fraction only sets the precision.
  const cellHasFraction = /\d+\/\d+/.test(text);

  return text.replace(
    /(^|[^A-Za-z0-9])(\d+\s*'\s*\d+(?:\.\d+)?(?:\s+\d+\/\d+)?\s*["″”]?|\d+\s*'|\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(?=[^A-Za-z0-9]|$)/g,
    (match, prefix, value) => {
      const measurementValue = parseMeasurementValue(value);
      if (measurementValue == null) return match;

      return `${prefix}${formatCentimeterValue(measurementValue, value, cellHasFraction)}`;
    }
  );
}

/**
 * Parse a measurement token like "34", "34.5", or "34 1/2".
 *
 * @param {string} value
 * @returns {number | null}
 */
function parseMeasurementValue(value) {
  const trimmedValue = value.trim();

  // 5' 5" / 5'6" / 6' — feet are 12 inches, so normalise to inches first.
  const feetMatch = trimmedValue.match(/^(\d+)\s*'(?:\s*(\d+(?:\.\d+)?(?:\s+\d+\/\d+)?)\s*["\u2033\u201d]?)?$/);
  if (feetMatch) {
    const inchesPart = feetMatch[2] ? parseMeasurementValue(feetMatch[2]) : 0;
    if (inchesPart == null) return null;

    return Number(feetMatch[1]) * 12 + inchesPart;
  }
  const fractionMatch = trimmedValue.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const wholeNumber = Number(fractionMatch[1] ?? 0);
    const numerator = Number(fractionMatch[2]);
    const denominator = Number(fractionMatch[3]);

    if (denominator === 0) return null;

    return wholeNumber + numerator / denominator;
  }

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

/**
 * Format centimeters: whole numbers for whole inches, one decimal place when the value or
 * anything else in its cell was fractional.
 *
 * @param {number} inches
 * @param {string} sourceValue
 * @param {boolean} [cellHasFraction]
 * @returns {string}
 */
function formatCentimeterValue(inches, sourceValue, cellHasFraction = false) {
  const centimeters = inches * 2.54;
  const keepDecimal = cellHasFraction || sourceValue.includes('.') || sourceValue.includes('/');
  const roundedValue = keepDecimal ? roundTo(centimeters, 1) : Math.round(centimeters);

  return Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(1).replace(/\.0$/, '');
}

/**
 * @param {number} value
 * @param {number} precision
 * @returns {number}
 */
function roundTo(value, precision) {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeText(value) {
  return (value ?? '').trim().replace(/\s+/g, ' ');
}
